import { useState } from 'react';
import { Plus, Folder as FolderIcon, MoreVertical, Pencil, Trash2, LayoutGrid, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFolders } from '@/hooks/useFolders';
import { cn } from '@/lib/utils';
import type { Folder } from '@/types';
import { toast } from 'sonner';

interface FolderManagerProps {
  projectId: string;
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
}

export function FolderManager({ projectId, selectedFolderId, onSelectFolder }: FolderManagerProps) {
  const { folders, isLoading, addFolder, updateFolder, deleteFolder } = useFolders(projectId);
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [isSaving, setIsSaving] = useState(false);

  const handleCreate = async () => {
    if (!formData.name.trim()) return;
    setIsSaving(true);
    try {
      await addFolder({
        name: formData.name,
        description: formData.description,
      });
      toast.success('文件夹已创建');
      setIsCreateOpen(false);
      setFormData({ name: '', description: '' });
    } catch (error) {
      toast.error('创建失败');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingFolder || !formData.name.trim()) return;
    setIsSaving(true);
    try {
      await updateFolder(editingFolder.id, {
        name: formData.name,
        description: formData.description,
      });
      toast.success('已更新');
      setEditingFolder(null);
    } catch (error) {
      toast.error('更新失败');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (folder: Folder) => {
    if (confirm(`确定要删除文件夹 "${folder.name}" 吗？文件夹内的素材不会被删除，将变为"未分类"。`)) {
      try {
        await deleteFolder(folder.id);
        if (selectedFolderId === folder.id) {
          onSelectFolder(null);
        }
        toast.success('文件夹已删除');
      } catch (error) {
        toast.error('删除失败');
      }
    }
  };

  const openEdit = (folder: Folder) => {
    setEditingFolder(folder);
    setFormData({ name: folder.name, description: folder.description || '' });
  };

  if (isLoading) return <div className="h-8 w-full animate-pulse bg-muted rounded-full max-w-[200px]" />;

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar">
      <div className="flex items-center gap-1.5 min-w-max">
        <Button
          variant={selectedFolderId === null ? "default" : "secondary"}
          size="sm"
          className={cn(
            "h-8 px-3 rounded-full font-medium transition-all",
            selectedFolderId === null ? "shadow-sm" : "hover:bg-muted bg-background border"
          )}
          onClick={() => onSelectFolder(null)}
        >
          <LayoutGrid className="w-3.5 h-3.5 mr-1.5" />
          全部素材
        </Button>

        {folders.map((folder) => (
          <div key={folder.id} className="group relative flex items-center">
            <Button
              variant={selectedFolderId === folder.id ? "default" : "secondary"}
              size="sm"
              className={cn(
                "h-8 pl-3 rounded-full font-medium transition-all flex items-center justify-start",
                selectedFolderId === folder.id ? "shadow-sm" : "hover:bg-muted bg-background border"
              )}
              style={{ paddingRight: '36px' }}
              onClick={() => onSelectFolder(folder.id)}
            >
              <FolderIcon className={cn("w-3.5 h-3.5 mr-1.5 flex-shrink-0", selectedFolderId === folder.id ? "fill-current/20" : "text-muted-foreground")} />
              <span className="truncate max-w-[100px] inline-block">{folder.name}</span>
            </Button>
            
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={cn(
                      "h-7 w-7 rounded-full transition-all",
                      selectedFolderId === folder.id 
                        ? "text-primary-foreground hover:bg-black/10" 
                        : "text-muted-foreground hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
                    )}
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-32">
                  <DropdownMenuItem onClick={() => openEdit(folder)}>
                    <Pencil className="w-3.5 h-3.5 mr-2" />
                    编辑
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(folder)}>
                    <Trash2 className="w-3.5 h-3.5 mr-2" />
                    删除
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}

        <div className="h-4 w-px bg-border mx-1" />

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 px-3 rounded-full border-dashed border hover:border-primary hover:text-primary transition-colors bg-background">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              分组
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>新建分组</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">分组名称</label>
                <Input 
                  placeholder="如：菜品特写、环境图、人像..." 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-sm font-medium">
                  给 AI 的描述
                  <Info className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <Textarea 
                  placeholder="描述此文件夹存放什么类型的素材，帮助 AI 更精准地检索。例如：这里存放的是火锅底料、食材的微距特写。" 
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>取消</Button>
              <Button onClick={handleCreate} disabled={isSaving || !formData.name}>创建</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* 编辑弹窗 */}
      <Dialog open={!!editingFolder} onOpenChange={(open) => !open && setEditingFolder(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>编辑分组</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">分组名称</label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                给 AI 的描述
                <Info className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingFolder(null)}>取消</Button>
            <Button onClick={handleUpdate} disabled={isSaving || !formData.name}>保存修改</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
